import React, { useState } from 'react';
import { C1SignupData } from '../C1SignupFlow';
import { BILLING_PLANS } from '../../../config/billingConfig';
import { PlanCard, BillingSummaryCard } from '../components';
import './StripeBilling.css';

interface StepProps {
  data: C1SignupData;
  onNext: (stepData: Partial<C1SignupData>) => void;
  onCancel: () => void;
}

type PlanKey = 'STARTER' | 'GROWTH' | 'ENTERPRISE' | 'OFFER';

const PLAN_CONFIGS: { key: PlanKey; badge?: string; accentColor?: string }[] = [
  { key: 'STARTER' },
  { key: 'GROWTH', badge: 'Popular', accentColor: '#5B21B6' },
  { key: 'ENTERPRISE', badge: 'Popular', accentColor: '#5B21B6' },
  { key: 'OFFER', badge: '★ Limited', accentColor: '#D97706' },
];

const fmtPrice = (cents: number) =>
  (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const StripeBilling: React.FC<StepProps> = ({ onNext, onCancel }) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('GROWTH');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleNext = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    const plan = BILLING_PLANS[selectedPlan];
    onNext({
      stripePlanType: selectedPlan,
      billingCycle: 'one-time',
      paymentMethod: 'card',
      creditsPurchased: plan.credits,
      totalAmount: plan.price,
    });
  };

  return (
    <div className="stripe-billing-container">
      {/* ── Credit Package Selection ── */}
      <div className="plan-selection-section">
        <div className="section-header">🎫 Select Credit Package</div>
        <p className="section-subtitle">
          Available until 30 June 2026 at our lowest-ever per-credit rate. Prices exclude GST.
        </p>

        <div className="plans-grid">
          {PLAN_CONFIGS.map(({ key, badge, accentColor }) => {
            const plan = BILLING_PLANS[key];
            return (
              <PlanCard
                key={key}
                name={plan.name}
                price={plan.price}
                credits={plan.credits}
                pricePerCredit={plan.pricePerCredit}
                features={plan.features}
                selected={selectedPlan === key}
                onSelect={() => setSelectedPlan(key)}
                badge={badge}
                accentColor={accentColor}
              />
            );
          })}
        </div>

        <div className="upsell-hint">
          <strong>Need more credits later?</strong> You can purchase additional credit packages from your account dashboard at any time.
        </div>
      </div>

      {/* ── Payment Info ── */}
      <div className="payment-method-section">
        <div className="section-header">💰 Payment</div>
        <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '13px', color: '#475569' }}>
          Payment is handled securely by Stripe. Available methods — card, Apple Pay, Google Pay, and bank transfer (BECS) — are shown automatically on the next step based on your browser and device.
        </div>
      </div>

      {/* ── Billing Summary ── */}
      <BillingSummaryCard
        rows={[
          { label: 'Selected Package:', value: BILLING_PLANS[selectedPlan].name },
          { label: 'Credits:', value: `${BILLING_PLANS[selectedPlan].credits} credits` },
          { label: 'Subtotal:', value: `$${fmtPrice(BILLING_PLANS[selectedPlan].price)} AUD` },
          { label: 'GST (10%):', value: `$${fmtPrice(Math.round(BILLING_PLANS[selectedPlan].price * 0.1))} AUD` },
          { label: 'Total (inc. GST):', value: `$${fmtPrice(Math.round(BILLING_PLANS[selectedPlan].price * 1.1))} AUD`, bold: true },
        ]}
      />

      {/* ── Action Buttons ── */}
      <div className="button-group">
        <button className="btn-secondary" data-testid="stripe-billing-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn-primary" data-testid="stripe-billing-continue-btn" onClick={handleNext} disabled={isProcessing}>
          {isProcessing ? 'Processing…' : 'Continue →'}
        </button>
      </div>
    </div>
  );
};

export default StripeBilling;
